import { Grid, TextField } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { BorderedFormGrid } from "../../../Forms/BorderedFormGrid";
import { textFieldDefaultProps } from "../../../Forms/TextFieldDefaultProps";
import { UPPERCASE_INPUT } from "../../../Forms/formStyles";
import { CountrySelect } from "../../../Locations/CountrySelect";
import { Branch } from "../../Branch";
import { branchSchema } from "./branchSchema";
import MdmAccordion from "../../../Layout/Accordion/MdmAccordion";
import { StyledIconButton } from "../../../Layout/Buttons/StyledIconButton";
import { useAddBranch } from "../../Hooks/useAddBranch";
import { useUpdateBranch } from "../../Hooks/useUpdateBranch";
import { useFetchCountries } from "../../../Locations/useFetchCountries";
import { useFetchBranchesForCarrier } from "../../Hooks/useFetchBranchesForCarrier";
import { resetFormStateAndKeepFormValues } from "../../../Forms/resetFormStateAndKeepFormValues";
import { isFormSubmitForbidden } from "../../../Forms/isFormSubmitForbidden";
import { ReadOnlyProps } from "../../../Project/EditProject/ReadOnlyProps";

type Props = {
  initialBranch: Branch;
  deleteBranch: (branch: Branch) => void;
};

export const BranchForm = ({ initialBranch, deleteBranch, isReadOnly }: Props & ReadOnlyProps) => {
  const methods = useForm<Branch>({
    resolver: yupResolver(branchSchema),
    defaultValues: initialBranch,
    mode: "all",
    disabled: isReadOnly,
  });

  const { control, getValues, watch, formState, handleSubmit, setValue } = methods;

  const [expanded, setExpanded] = useState<boolean>(initialBranch.IsNewBranch ?? false);
  const toggleExpanded = () => setExpanded((prev) => !prev);

  const { mutateAsync: addBranch } = useAddBranch();
  const { mutateAsync: updateBranch } = useUpdateBranch();
  const { refetch: refetchBranches } = useFetchBranchesForCarrier(initialBranch.CarrierId);

  const saveBranch = (branch: Branch) => {
    const save = branch.IsNewBranch ? addBranch(branch) : updateBranch(branch);
    save.then(async () => {
      await refetchBranches();
      resetFormStateAndKeepFormValues(methods);
    });
  };

  const saveBranchButton = (
    <StyledIconButton
      key="save"
      onClick={(e) => {
        e.stopPropagation();
        handleSubmit((values: Branch) => saveBranch(values))();
      }}
      disabled={isReadOnly || isFormSubmitForbidden(formState)}
    >
      <SaveIcon />
    </StyledIconButton>
  );

  const deleteBranchButton = (
    <StyledIconButton
      key="delete"
      onClick={async (e) => {
        e.stopPropagation();
        await deleteBranch(getValues());
      }}
      disabled={isReadOnly}
    >
      <DeleteIcon />
    </StyledIconButton>
  );

  const { data: countries } = useFetchCountries();
  const updateIsoCode = useCallback(
    (countryId: string | number) => {
      const selectedCountry = countries?.find((country) => country.Id === Number(countryId));
      setValue("CountryIsoCode", selectedCountry?.IsoCode ?? "");
    },
    [countries, methods]
  );
  useEffect(
    () => updateIsoCode(initialBranch.CountryId),
    [initialBranch.CountryId, countries, updateIsoCode]
  );

  const isExistingBranch = !watch("IsNewBranch");
  const controls = isExistingBranch ? [saveBranchButton, deleteBranchButton] : [saveBranchButton];

  const getBranchReference = (branch: Branch) => {
    return `${branch.BranchGsdb}-${branch.ImcsCode}`;
  };

  return (
    // TODO - can we just use <form> here?
    <FormProvider {...methods}>
      <MdmAccordion
        title={isReadOnly ? initialBranch.Name : getValues("Name")}
        controls={controls}
        expanded={expanded}
        onChange={toggleExpanded}
      >
        <BorderedFormGrid>
          <Grid item xs={3}>
            <Controller
              name="Name"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...textFieldDefaultProps}
                  label="Branch Name"
                  required
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={3}>
            <Controller
              name="BranchGsdb"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...textFieldDefaultProps}
                  label="Branch GSDB"
                  required
                  error={!!error}
                  helperText={error?.message}
                  disabled={isExistingBranch}
                  inputProps={UPPERCASE_INPUT}
                />
              )}
            />
          </Grid>

          <Grid item xs={3}>
            <Controller
              name="ImcsCode"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...textFieldDefaultProps}
                  label="IMCS Code"
                  error={!!error}
                  helperText={error?.message}
                  inputProps={UPPERCASE_INPUT}
                />
              )}
            />
          </Grid>

          <Grid item xs={3}>
            <TextField
              {...textFieldDefaultProps}
              label="Reference"
              disabled
              value={
                isReadOnly
                  ? getBranchReference(initialBranch)
                  : `${watch("BranchGsdb")}-${watch("ImcsCode")}`
              }
              inputProps={UPPERCASE_INPUT}
            />
          </Grid>
        </BorderedFormGrid>

        <BorderedFormGrid>
          <Grid item xs={3}>
            <Controller
              name="Street"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...textFieldDefaultProps}
                  label="Street"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={3}>
            <Controller
              name="CountryIsoCode"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...textFieldDefaultProps}
                  label="ISO Country Code"
                  error={!!error}
                  helperText={error?.message}
                  disabled
                />
              )}
            />
          </Grid>
          {/* PLACEHOLDER */}
          <Grid item xs={6} />

          <Grid item xs={3}>
            <Controller
              name="Zip"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...textFieldDefaultProps}
                  label="Postcode"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={3}>
            {/* PLACEHOLDER FOR FIELD TIMEZONE */}
          </Grid>
          {/* PLACEHOLDER */}
          <Grid item xs={6} />

          <Grid item xs={3}>
            <Controller
              name="City"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...textFieldDefaultProps}
                  label="City"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={3}>
            {/* PLACEHOLDER FOR FIELD TYPE */}
          </Grid>
          {/* PLACEHOLDER */}
          <Grid item xs={6} />

          <Grid item xs={3}>
            <Controller
              name="Region"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...textFieldDefaultProps}
                  label="Region"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          {/* PLACEHOLDER */}
          <Grid item xs={9} />

          <Grid item xs={3}>
            <Controller
              name="Area"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...textFieldDefaultProps}
                  label="Area"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          {/* PLACEHOLDER */}
          <Grid item xs={9} />

          <Grid item xs={3}>
            <CountrySelect
              control={control}
              name="CountryId"
              onCountryChanged={updateIsoCode}
              required
            />
          </Grid>
        </BorderedFormGrid>
      </MdmAccordion>
    </FormProvider>
  );
};
